import React, { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  Play, Pause, RefreshCcw, Share2, Volume2, VolumeX,
} from 'lucide-react';
import {
  FilmReel, useFilmClock, currentScene, TOTAL,
} from '../components/watch/FilmReel';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Iter 150 · CaraLoom 30-second demo film.
 * Iter 151 · TTS voice-over (OpenAI ``tts-1-hd`` / ``onyx``) + captions.
 * Iter 152 · Refactored — scene rendering + clock extracted to
 *            ``components/watch/FilmReel`` so the landing hero can
 *            embed the same reel muted-and-looped.
 *
 * This page handles the chrome unique to the full film:
 *   * player controls (play / pause / restart)
 *   * TTS narration audio + drift-corrected timeline sync
 *   * WCAG AA subtitle overlay
 *   * share button + mute toggle
 *
 * Route: /watch
 */

export default function WatchDemoPage() {
  const [playing, setPlaying] = useState(true);
  // Autoplay policy — audio starts muted so the film plays on load
  // in every browser; the top-right pill unmutes on demand.
  const [muted, setMuted] = useState(true);
  const [captions, setCaptions] = useState([]);
  const [elapsed, setElapsed, startRef] = useFilmClock({ playing });
  const audioRef = useRef(null);

  // Preload caption timings for WCAG AA subtitles.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/watch/narration/meta?script=v1`);
        if (!r.ok) return;
        const d = await r.json();
        if (!cancelled) setCaptions(d.captions || []);
      } catch { /* offline — captions hide */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Drift-correct audio to the film clock. Only seek when the
  // desync is >0.3s so a healthy 20 ms jitter doesn't cause pops.
  useEffect(() => {
    if (!audioRef.current) return;
    if (!playing) {
      audioRef.current.pause();
      return;
    }
    const drift = Math.abs(audioRef.current.currentTime - elapsed);
    if (drift > 0.3) audioRef.current.currentTime = elapsed;
    audioRef.current.play().catch(() => { /* autoplay blocked */ });
  }, [playing, elapsed >= TOTAL - 0.1]);

  // Restart audio to 0 whenever the film loops.
  useEffect(() => {
    if (elapsed < 0.2 && audioRef.current) {
      audioRef.current.currentTime = 0;
      if (playing && !muted) audioRef.current.play().catch(() => {});
    }
  }, [elapsed < 0.2, playing, muted]);

  const restart = () => {
    setElapsed(0);
    startRef.current = Date.now();
    setPlaying(true);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    }
  };

  const toggleMute = () => {
    setMuted((m) => {
      if (audioRef.current) audioRef.current.muted = !m;
      return !m;
    });
  };

  const share = async () => {
    const url = `${window.location.origin}/watch`;
    try {
      if (navigator.share) {
        await navigator.share({ title: 'CaraLoom · 30s demo', url });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch { /* user cancelled */ }
  };

  const activeCaption = captions.find(
    (c) => elapsed >= c.start && elapsed < c.end,
  );

  return (
    <div className="min-h-screen bg-black text-slate-100 flex flex-col" data-testid="watch-demo-page">
      <audio
        ref={audioRef}
        src={`${API}/watch/narration.mp3?script=v1`}
        preload="auto"
        muted={muted}
        data-testid="watch-narration-audio"
      />

      {/* Chrome */}
      <div className="border-b border-slate-800/70 px-6 py-4 flex items-center justify-between">
        <Link to="/" className="text-sm text-slate-400 hover:text-slate-200 flex items-center gap-2" data-testid="watch-home-link">
          <span className="font-semibold tracking-tight">CaraLoom</span>
          <span className="text-slate-600">·</span>
          <span>30-second demo</span>
        </Link>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMute}
            className={`text-xs flex items-center gap-1.5 rounded-md px-3 py-1.5 border transition-colors ${
              muted
                ? 'text-indigo-200 border-indigo-500/60 bg-indigo-500/20 hover:bg-indigo-500/30'
                : 'text-slate-300 border-slate-800/70 hover:text-slate-100'
            }`}
            data-testid="watch-mute-btn"
            aria-label={muted ? 'Turn sound on' : 'Mute'}
          >
            {muted ? <VolumeX className="h-3.5 w-3.5" /> : <Volume2 className="h-3.5 w-3.5" />}
            {muted ? 'Click for sound' : 'Sound on'}
          </button>
          <button
            onClick={share}
            className="text-xs text-slate-400 hover:text-slate-200 flex items-center gap-1.5 rounded-md px-3 py-1.5 border border-slate-800/70"
            data-testid="watch-share-btn"
          >
            <Share2 className="h-3.5 w-3.5" /> Share
          </button>
        </div>
      </div>

      {/* Film frame */}
      <div className="flex-1 flex items-center justify-center p-4 md:p-10">
        <div className="relative w-full max-w-6xl">
          <FilmReel
            elapsed={elapsed}
            startRefKey={Math.floor((startRef.current || 0) / 1000)}
            mini={false}
            ctaEnabled
          />

          {/* Captions — WCAG AA compliance for the voice-over */}
          {activeCaption && (
            <div
              className="pointer-events-none absolute inset-x-0 bottom-8 z-40 flex justify-center px-8"
              data-testid="watch-caption"
            >
              <div
                key={activeCaption.text}
                className="anim-fade-up max-w-3xl rounded-lg bg-black/70 backdrop-blur-sm border border-white/10 px-4 py-2 text-center text-sm md:text-base text-slate-100"
              >
                {activeCaption.text}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Player controls */}
      <div className="border-t border-slate-800/70 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-xs text-slate-500 tabular-nums">
          <span data-testid="watch-elapsed">{elapsed.toFixed(1)}s</span>
          <span>/</span>
          <span>{TOTAL.toFixed(1)}s</span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setPlaying(p => !p)}
            className="rounded-md border border-slate-800 hover:bg-slate-900 h-9 w-9 flex items-center justify-center"
            data-testid="watch-playpause"
            aria-label={playing ? 'Pause' : 'Play'}
          >
            {playing ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4" />}
          </button>
          <button
            onClick={restart}
            className="rounded-md border border-slate-800 hover:bg-slate-900 h-9 w-9 flex items-center justify-center"
            data-testid="watch-restart"
            aria-label="Restart"
          >
            <RefreshCcw className="h-4 w-4" />
          </button>
        </div>
        <div className="text-xs text-slate-500">
          <span className="hidden md:inline">Screen-record with QuickTime · OBS to export MP4.</span>
        </div>
      </div>
    </div>
  );
}

// Keep this reference so callers importing `currentScene` for tests
// keep working after the refactor.
export { currentScene };
